/**
 * assets/quick-order-page.js
 *
 * Client-side behaviour for the Quick Order page (sections/quick-order-page.liquid).
 * Standalone — no dependency on Dawn's global.js, although it happily coexists
 * with Dawn's <quantity-input> element, whose plus/minus buttons dispatch
 * bubbling `change` events that are handled below.
 *
 * Responsibilities:
 *  - Real-time search (debounced 250ms) + collection filter + "in cart" toggle.
 *  - Line total / footer total recalculation, including volume pricing from
 *    variant.quantity_price_breaks so estimates match what the cart will charge.
 *  - Enforcing quantity rules (min / max / increment) by snapping invalid input.
 *  - Batch add-to-cart via POST /cart/add.js. Because /cart/add.js *appends*
 *    to the cart and rows are pre-filled with in-cart quantities, only the
 *    positive delta (input minus what is already in the cart) is submitted —
 *    otherwise re-submitting the form would double every quantity.
 *  - Refreshing the header cart bubble via the Section Rendering API payload
 *    bundled into the /cart/add.js response.
 *  - Syncing inputs with /cart.js on load and after each add.
 */

(function () {
  'use strict';

  var FILTER_DEBOUNCE_MS = 250;
  var FADE_MS = 160;

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  /**
   * Minimal Shopify money formatter. `format` is the store's money format
   * string (e.g. "${{amount}}"), passed in from Liquid via a data attribute.
   */
  function formatMoney(cents, format) {
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';

      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var decimals = parts[1] ? decimal + parts[1] : '';
      return units + decimals;
    }

    var value = '';
    switch ((formatString.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  class QuickOrderPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.strings = {};
      var stringsEl = this.querySelector('[data-quick-order-strings]');
      if (stringsEl) {
        try {
          this.strings = JSON.parse(stringsEl.textContent) || {};
        } catch (error) {
          this.strings = {};
        }
      }

      this.searchInput = this.querySelector('[data-quick-order-search]');
      this.filterSelect = this.querySelector('[data-quick-order-filter]');
      this.inCartToggle = this.querySelector('[data-quick-order-incart-toggle]');
      this.visibleCountEl = this.querySelector('[data-quick-order-visible-count]');
      this.noResultsEl = this.querySelector('[data-quick-order-no-results]');
      this.tableWrapper = this.querySelector('.quick-order__table-wrapper');
      this.totalItemsEl = this.querySelector('[data-quick-order-total-items]');
      this.subtotalEl = this.querySelector('[data-quick-order-subtotal]');
      this.submitButton = this.querySelector('[data-quick-order-submit]');
      this.clearButton = this.querySelector('[data-quick-order-clear]');
      this.statusEl = this.querySelector('[data-quick-order-status]');
      this.overlay = this.querySelector('[data-quick-order-overlay]');

      this.rows = [];
      this.stateByInput = new Map();
      var rowElements = this.querySelectorAll('[data-quick-order-row]');
      for (var i = 0; i < rowElements.length; i++) {
        var state = this.buildRowState(rowElements[i]);
        this.rows.push(state);
        if (state.input) this.stateByInput.set(state.input, state);
      }

      // Delegated quantity handling: typed input + direct click handling for
      // the +/- buttons (not relying on Dawn's <quantity-input> custom element
      // which may not initialise in time on every theme version).
      this.addEventListener('input', (event) => {
        var rowState = this.stateByInput.get(event.target);
        if (rowState) this.onQuantityInput(rowState);
      });
      this.addEventListener('change', (event) => {
        var rowState = this.stateByInput.get(event.target);
        if (rowState) this.onQuantityChange(rowState);
      });
      this.addEventListener('click', (event) => {
        var button = event.target.closest('.quantity__button');
        if (!button) return;
        event.preventDefault();
        var input = button.closest('.quick-order__quantity');
        if (!input) return;
        input = input.querySelector('[data-quick-order-input]');
        if (!input) return;
        var rowState = this.stateByInput.get(input);
        if (!rowState) return;
        var current = parseInt(input.value, 10) || 0;
        var step = rowState.step || 1;
        var min = rowState.min || 1;
        if (button.name === 'plus') {
          input.value = current === 0 ? min : current + step;
        } else {
          input.value = current - step < min ? 0 : current - step;
        }
        rowState.touched = true;
        this.recalcRow(rowState);
        this.updateTotals();
      });

      // Keyboard navigation: Enter jumps to the next visible row's input,
      // Shift+Enter goes back. Value is auto-selected so the buyer can
      // immediately type a new number.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        var rowState = this.stateByInput.get(event.target);
        if (!rowState) return;
        event.preventDefault();
        this.navigateInput(rowState, event.shiftKey ? -1 : 1);
      });

      // Warn before navigating away with unsaved quantities.
      this.beforeUnload = (event) => {
        var hasUnsaved = this.rows.some(function (state) {
          return state.touched && state.qty !== state.inCart;
        });
        if (hasUnsaved) {
          event.preventDefault();
          event.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', this.beforeUnload);

      if (this.searchInput) {
        this.searchInput.addEventListener(
          'input',
          debounce(() => this.applyFilters(), FILTER_DEBOUNCE_MS)
        );
      }
      if (this.filterSelect) this.filterSelect.addEventListener('change', () => this.applyFilters());
      if (this.inCartToggle) this.inCartToggle.addEventListener('change', () => this.applyFilters());
      if (this.submitButton) this.submitButton.addEventListener('click', () => this.onSubmit());
      if (this.clearButton) this.clearButton.addEventListener('click', () => this.onClear());

      this.onResize = debounce(() => this.setStickyOffset(), 150);
      window.addEventListener('resize', this.onResize);

      // Refresh cart state when returning via the browser back/forward cache.
      this.onPageShow = (event) => {
        if (event.persisted) this.syncWithCart();
      };
      window.addEventListener('pageshow', this.onPageShow);

      this.setStickyOffset();
      this.rows.forEach((rowState) => this.recalcRow(rowState));
      this.updateTotals();
      this.applyFilters(true);
      this.syncWithCart();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('beforeunload', this.beforeUnload);
    }

    buildRowState(row) {
      var input = row.querySelector('[data-quick-order-input]');

      var breaks = [];
      if (row.dataset.priceBreaks) {
        try {
          breaks = JSON.parse(row.dataset.priceBreaks) || [];
        } catch (error) {
          breaks = [];
        }
      }
      // Highest minimum first so the first match is the applicable break.
      breaks.sort(function (a, b) {
        return b.minQty - a.minQty;
      });

      return {
        row: row,
        input: input,
        id: Number(row.dataset.variantId),
        price: Number(row.dataset.price) || 0,
        breaks: breaks,
        breaksAsc: breaks.slice().sort(function (a, b) { return a.minQty - b.minQty; }),
        breakEls: Array.prototype.slice.call(row.querySelectorAll('[data-quick-order-break]')),
        nudgeEl: row.querySelector('[data-quick-order-nudge]'),
        nudgeText: row.querySelector('[data-quick-order-nudge-text]'),
        nudgeFill: row.querySelector('[data-quick-order-nudge-fill]'),
        min: input ? parseInt(input.dataset.qtyMin, 10) || 1 : 1,
        max: input && input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : null,
        step: input ? parseInt(input.getAttribute('step'), 10) || 1 : 1,
        search: (row.dataset.search || '').toLowerCase(),
        collections: (row.dataset.collections || '').trim().split(/\s+/).filter(Boolean),
        productTitle: row.dataset.productTitle || '',
        inCart: parseInt(row.dataset.inCart, 10) || 0,
        totalEl: row.querySelector('[data-quick-order-line-total]'),
        badge: row.querySelector('[data-quick-order-incart-badge]'),
        badgeCount: row.querySelector('[data-quick-order-incart-badge] [data-count]'),
        qty: input ? parseInt(input.value, 10) || 0 : 0,
        lineTotal: 0,
        visible: true,
        touched: false,
      };
    }

    /* ------------------------------------------------------------------ *
     * Quantities and totals
     * ------------------------------------------------------------------ */

    onQuantityInput(state) {
      state.touched = true;
      this.recalcRow(state);
      this.updateTotals();
    }

    onQuantityChange(state) {
      state.touched = true;
      var snapped = this.snapQuantity(state, state.input.value);
      if (String(snapped) !== state.input.value) state.input.value = snapped;
      this.recalcRow(state);
      this.updateTotals();
      if (this.inCartToggle && this.inCartToggle.checked) this.applyFilters();
    }

    /**
     * Snap a raw value to the variant's quantity rule. Zero ("not ordering
     * this line") is always allowed; any positive value is rounded to the
     * nearest increment, raised to the minimum, and capped at the maximum.
     */
    snapQuantity(state, rawValue) {
      var value = parseInt(rawValue, 10);
      if (isNaN(value) || value <= 0) return 0;

      var step = state.step > 0 ? state.step : 1;
      var min = state.min > 0 ? state.min : 1;

      value = Math.round(value / step) * step;
      if (value < min) value = min;

      if (state.max !== null && value > state.max) {
        value = state.max - ((state.max - min) % step);
      }

      return Math.max(value, 0);
    }

    /**
     * Unit price for a given quantity, taking quantity_price_breaks into
     * account so line totals estimate what Shopify will actually charge.
     * Also highlights whichever break note is currently applied.
     */
    effectiveUnitPrice(state, qty) {
      var unitPrice = state.price;
      var appliedMin = null;

      for (var i = 0; i < state.breaks.length; i++) {
        if (qty >= state.breaks[i].minQty) {
          unitPrice = state.breaks[i].price;
          appliedMin = state.breaks[i].minQty;
          break;
        }
      }

      state.breakEls.forEach(function (el) {
        el.classList.toggle(
          'quick-order__break--active',
          appliedMin !== null && Number(el.dataset.minQty) === appliedMin
        );
      });

      return unitPrice;
    }

    recalcRow(state) {
      if (!state.input) return;
      var qty = Math.max(parseInt(state.input.value, 10) || 0, 0);
      state.qty = qty;
      state.lineTotal = this.effectiveUnitPrice(state, qty) * qty;
      if (state.totalEl) state.totalEl.textContent = formatMoney(state.lineTotal, this.moneyFormat);
      this.updateNudge(state);
    }

    /**
     * Dynamic volume-pricing nudge. Shows a loss-aversion message with a
     * progress bar when the buyer is partway to the next price break, and
     * a confirmation when all breaks are met.
     */
    updateNudge(state) {
      if (!state.nudgeEl || state.breaksAsc.length === 0) return;
      var qty = state.qty;
      var fmt = this.moneyFormat;

      // qty is 0 — hide the nudge, let the static break text stand alone.
      if (qty === 0) {
        state.nudgeEl.classList.add('hidden');
        state.nudgeEl.style.setProperty('--nudge-progress', '0%');
        return;
      }

      // Determine current effective unit price.
      var currentUnitPrice = state.price;
      for (var i = 0; i < state.breaks.length; i++) {
        if (qty >= state.breaks[i].minQty) {
          currentUnitPrice = state.breaks[i].price;
          break;
        }
      }

      // Find the next unmet break (lowest threshold above current qty).
      var nextBreak = null;
      for (var j = 0; j < state.breaksAsc.length; j++) {
        if (qty < state.breaksAsc[j].minQty) {
          nextBreak = state.breaksAsc[j];
          break;
        }
      }

      if (nextBreak) {
        // Progress toward the next tier — loss-aversion framing.
        var remaining = nextBreak.minQty - qty;
        var saving = currentUnitPrice - nextBreak.price;
        var pct = Math.min(Math.round((qty / nextBreak.minQty) * 100), 100);

        var template = this.strings.nudgeRemaining || '[remaining] more to save [saving]/unit';
        state.nudgeText.textContent = template
          .replace('[remaining]', remaining)
          .replace('[saving]', formatMoney(saving, fmt));
        state.nudgeEl.style.setProperty('--nudge-progress', pct + '%');
        state.nudgeEl.classList.remove('hidden', 'quick-order__nudge--unlocked');
      } else {
        // All breaks met — show confirmed savings vs base price.
        var totalSaving = state.price - currentUnitPrice;
        var unlockedTemplate = this.strings.nudgeUnlocked || 'Saving [saving]/unit';
        state.nudgeText.textContent = unlockedTemplate
          .replace('[saving]', formatMoney(totalSaving, fmt));
        state.nudgeEl.style.setProperty('--nudge-progress', '100%');
        state.nudgeEl.classList.remove('hidden');
        state.nudgeEl.classList.add('quick-order__nudge--unlocked');
      }
    }

    updateTotals() {
      var units = 0;
      var subtotal = 0;
      this.rows.forEach(function (state) {
        units += state.qty;
        subtotal += state.lineTotal;
      });
      if (this.totalItemsEl) this.totalItemsEl.textContent = units;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(subtotal, this.moneyFormat);
    }

    /* ------------------------------------------------------------------ *
     * Search and filtering
     * ------------------------------------------------------------------ */

    applyFilters(immediate) {
      var term = (this.searchInput ? this.searchInput.value : '').trim().toLowerCase();
      var collection = this.filterSelect ? this.filterSelect.value : '';
      var selectedOnly = Boolean(this.inCartToggle && this.inCartToggle.checked);

      var toHide = [];
      var toShow = [];

      this.rows.forEach(function (state) {
        var match = true;
        if (term) match = state.search.indexOf(term) !== -1;
        if (match && collection) match = state.collections.indexOf(collection) !== -1;
        if (match && selectedOnly) match = state.qty > 0 || state.inCart > 0;
        state.visible = match;
        (match ? toShow : toHide).push(state.row);
      });

      clearTimeout(this.filterTimer);

      var self = this;
      var commit = function () {
        toHide.forEach(function (row) {
          row.classList.add('hidden');
        });
        toShow.forEach(function (row) {
          row.classList.remove('hidden');
        });
        requestAnimationFrame(function () {
          toShow.forEach(function (row) {
            row.classList.remove('quick-order__row--fading');
          });
        });
        self.updateVisibleState();
      };

      // Fade rows out before removing them from the layout.
      toHide.forEach(function (row) {
        row.classList.add('quick-order__row--fading');
      });

      if (immediate || this.reducedMotion) {
        commit();
      } else {
        this.filterTimer = setTimeout(commit, FADE_MS);
      }
    }

    updateVisibleState() {
      var total = this.rows.length;
      var visible = 0;
      this.rows.forEach(function (state) {
        if (state.visible) visible++;
      });

      if (this.visibleCountEl) {
        var template = this.strings.showing || 'Showing [count] of [total] items';
        this.visibleCountEl.textContent = template
          .replace('[count]', visible)
          .replace('[total]', total);
      }
      if (this.noResultsEl) this.noResultsEl.classList.toggle('hidden', visible > 0);
      if (this.tableWrapper) this.tableWrapper.classList.toggle('hidden', visible === 0);
    }

    /* ------------------------------------------------------------------ *
     * Cart state
     * ------------------------------------------------------------------ */

    syncWithCart() {
      var self = this;
      return fetch(this.cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart fetch failed');
          return response.json();
        })
        .then(function (cart) {
          var counts = {};
          (cart.items || []).forEach(function (item) {
            counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
          });

          self.rows.forEach(function (state) {
            var cartQty = counts[state.id] || 0;
            state.inCart = cartQty;

            if (state.badge) {
              state.badge.classList.toggle('hidden', cartQty === 0);
              if (state.badgeCount) state.badgeCount.textContent = cartQty;
            }

            // Pre-populate inputs from the cart, but never clobber a value
            // the buyer has typed themselves in this session.
            if (state.input && !state.touched) {
              var current = parseInt(state.input.value, 10) || 0;
              if (current !== cartQty) {
                state.input.value = cartQty;
                self.recalcRow(state);
              }
            }
          });

          self.updateTotals();
          if (self.inCartToggle && self.inCartToggle.checked) self.applyFilters(true);
        })
        .catch(function () {
          /* Non-fatal: the server-rendered quantities remain in place. */
        });
    }

    /* ------------------------------------------------------------------ *
     * Add to cart
     * ------------------------------------------------------------------ */

    onSubmit() {
      var additions = [];
      var selectedUnits = 0;
      var hasReductions = false;

      this.rows.forEach(function (state) {
        if (!state.input) return;
        selectedUnits += state.qty;
        if (state.qty > state.inCart) {
          // /cart/add.js appends, so submit only the difference between the
          // desired quantity and what's already in the cart.
          additions.push({ id: state.id, quantity: state.qty - state.inCart });
        } else if (state.qty < state.inCart) {
          hasReductions = true;
        }
      });

      if (additions.length === 0) {
        var message =
          selectedUnits > 0
            ? this.strings.alreadyInCart || 'Everything you selected is already in your cart.'
            : this.strings.nothingSelected || 'Set a quantity on at least one item first.';
        if (hasReductions && this.strings.reduceHint) message += ' ' + this.strings.reduceHint;
        this.showStatus('info', message);
        return;
      }

      this.hideStatus();
      this.clearErrorRows();
      this.setLoading(true);

      var self = this;
      var unitsAdded = additions.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);

      fetch(this.cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: additions,
          sections: 'cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              if (!response.ok) {
                var error = new Error(data.description || data.message || '');
                error.data = data;
                throw error;
              }
              return data;
            });
        })
        .then(function (data) {
          self.updateCartIconBubble(data.sections);

          // Inputs now represent the cart itself; let syncWithCart own them.
          self.rows.forEach(function (state) {
            state.touched = false;
          });

          var successTemplate = self.strings.added || 'Added [count] item(s) to your cart.';
          var successMessage = successTemplate.replace('[count]', unitsAdded);
          if (hasReductions && self.strings.reduceHint) {
            successMessage += ' ' + self.strings.reduceHint;
          }
          self.showStatus('success', successMessage, true);

          return self.syncWithCart();
        })
        .catch(function (error) {
          var errorMessage =
            (error && error.message) ||
            self.strings.cartError ||
            'Something went wrong adding items to your cart.';
          self.highlightErrorRows(errorMessage, additions);
          self.showStatus('error', errorMessage);
          // Re-sync in case the request partially succeeded.
          return self.syncWithCart();
        })
        .then(function () {
          self.setLoading(false);
        });
    }

    onClear() {
      var self = this;
      this.rows.forEach(function (state) {
        if (!state.input) return;
        state.input.value = '0';
        state.touched = true;
        self.recalcRow(state);
      });
      this.updateTotals();
      this.hideStatus();
      this.clearErrorRows();
      if (this.inCartToggle && this.inCartToggle.checked) this.applyFilters(true);
    }

    /* ------------------------------------------------------------------ *
     * UI helpers
     * ------------------------------------------------------------------ */

    /**
     * Move focus to the next (direction = 1) or previous (direction = -1)
     * visible row's quantity input. Skips sold-out and filtered-out rows.
     * Auto-selects the value so the buyer can immediately type.
     */
    navigateInput(currentState, direction) {
      var currentIndex = this.rows.indexOf(currentState);
      if (currentIndex === -1) return;

      var i = currentIndex + direction;
      while (i >= 0 && i < this.rows.length) {
        var candidate = this.rows[i];
        if (candidate.input && candidate.visible && !candidate.row.classList.contains('hidden')) {
          candidate.input.focus();
          candidate.input.select();
          return;
        }
        i += direction;
      }
    }

    updateCartIconBubble(sections) {
      if (!sections || !sections['cart-icon-bubble']) return;
      var target = document.getElementById('cart-icon-bubble');
      if (!target) return;

      var parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var source = parsed.querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    }

    showStatus(type, message, withLinks) {
      if (!this.statusEl) return;

      this.statusEl.classList.remove(
        'hidden',
        'quick-order__status--success',
        'quick-order__status--error',
        'quick-order__status--info'
      );
      this.statusEl.classList.add('quick-order__status--' + type);
      this.statusEl.textContent = '';
      this.statusEl.appendChild(document.createTextNode(message + (withLinks ? ' ' : '')));

      if (withLinks) {
        var cartLink = document.createElement('a');
        cartLink.href = this.cartUrl;
        cartLink.className = 'link underlined-link';
        cartLink.textContent = this.strings.viewCart || 'View cart';
        this.statusEl.appendChild(cartLink);

        this.statusEl.appendChild(document.createTextNode(' · '));

        var checkoutLink = document.createElement('a');
        checkoutLink.href = '/checkout';
        checkoutLink.className = 'link underlined-link';
        checkoutLink.textContent = this.strings.checkout || 'Proceed to checkout';
        this.statusEl.appendChild(checkoutLink);
      }
    }

    hideStatus() {
      if (this.statusEl) this.statusEl.classList.add('hidden');
    }

    /**
     * Best-effort mapping of a /cart/add.js error (e.g. insufficient stock)
     * back to rows: Shopify's error description references the product title.
     */
    highlightErrorRows(message, additions) {
      var lower = (message || '').toLowerCase();
      if (!lower) return;

      var addedIds = additions.map(function (item) {
        return item.id;
      });

      this.rows.forEach(function (state) {
        if (addedIds.indexOf(state.id) === -1) return;
        if (state.productTitle && lower.indexOf(state.productTitle.toLowerCase()) !== -1) {
          state.row.classList.add('quick-order__row--error');
        }
      });
    }

    clearErrorRows() {
      this.rows.forEach(function (state) {
        state.row.classList.remove('quick-order__row--error');
      });
    }

    setLoading(isLoading) {
      if (this.overlay) this.overlay.classList.toggle('hidden', !isLoading);
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
      if (this.clearButton) this.clearButton.disabled = isLoading;
    }

    /**
     * Keep the sticky toolbar clear of Dawn's sticky header when the header
     * is configured to stay visible while scrolling.
     */
    setStickyOffset() {
      var offset = 0;
      var stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) {
        var type = stickyHeader.dataset.stickyType;
        if (type === 'always' || type === 'reduce-logo-size') {
          offset = Math.round(stickyHeader.getBoundingClientRect().height);
        }
      }
      this.style.setProperty('--quick-order-sticky-offset', offset + 'px');
    }
  }

  if (!customElements.get('quick-order-page')) {
    customElements.define('quick-order-page', QuickOrderPage);
  }
})();
